import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import { SupportTicketId as SupportTicketIdSchema } from "@redotech/effect-api-shared/ids/definitions";
import type {
  SupportTicketListItemResponse as SupportTicketListItemResponseType,
  UpdateSupportTicketInput,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { BasicIndex } from "@tanstack/db";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { createCollection } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import {
  buildSupportTicketListUrlParams,
  toUpdateSupportTicketInput,
} from "./support-ticket-list-requests";
import {
  supportTicketQueryScopeKey,
  type SupportTicketQueryScope,
} from "./support-ticket-query-keys";

const FIVE_MINUTES_MS = 5 * 60 * 1000;

export const supportTicketListQueryKeys = {
  all: ["supportTickets", "list"] as const,
  list: (scope?: SupportTicketQueryScope) =>
    [
      ...supportTicketListQueryKeys.all,
      ...supportTicketQueryScopeKey(scope),
    ] as const,
};

export interface SupportTicketListApi {
  getSupportTicket(
    supportTicketId: SupportTicketId,
  ): Promise<SupportTicketListItemResponseType>;
  listSupportTickets(params: {
    limit: number;
    offset: number;
    filterGroup?: string;
  }): Promise<{
    items: readonly SupportTicketListItemResponseType[];
    totalCount: number;
  }>;
  updateSupportTicket(
    supportTicketId: SupportTicketId,
    payload: UpdateSupportTicketInput,
  ): Promise<void>;
}

export interface CreateSupportTicketListCollectionOptions {
  queryClient: QueryClient;
  api: SupportTicketListApi;
  scope?: SupportTicketQueryScope;
  onUpdateError?: (supportTicketId: string, error: Error) => void;
}

export interface PageLoadResult {
  items: SupportTicketListItemResponseType[];
  totalCount: number;
  page: number;
}

export async function fetchSingleSupportTicket(
  api: SupportTicketListApi,
  supportTicketId: SupportTicketId,
): Promise<SupportTicketListItemResponseType> {
  return api.getSupportTicket(supportTicketId);
}

export function createSupportTicketListCollection({
  queryClient,
  api,
  scope,
  onUpdateError,
}: CreateSupportTicketListCollectionOptions) {
  const queryKey = supportTicketListQueryKeys.list(scope);
  const collection = createCollection(
    queryCollectionOptions({
      queryKey,
      syncMode: "eager",
      autoIndex: "eager",
      defaultIndexType: BasicIndex,
      queryFn: async (): Promise<SupportTicketListItemResponseType[]> =>
        queryClient.getQueryData<SupportTicketListItemResponseType[]>(
          queryKey,
        ) ?? [],
      getKey: (item) => item.id,
      queryClient,
      staleTime: FIVE_MINUTES_MS,
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const supportTicketId = SupportTicketIdSchema.make(
            String(mutation.key),
          );
          const payload = toUpdateSupportTicketInput(mutation.changes);
          try {
            await api.updateSupportTicket(supportTicketId, payload);
          } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            onUpdateError?.(String(mutation.key), error);
            throw error;
          }
        }

        return { refetch: false };
      },
    }),
  );

  collection.startSyncImmediate();

  const fetchPage = async (
    page: number,
    filterGroup: string | undefined,
    pageSize?: number,
  ): Promise<PageLoadResult> => {
    const result = await api.listSupportTickets(
      buildSupportTicketListUrlParams(page, filterGroup, pageSize),
    );

    collection.utils.writeBatch(() => {
      for (const item of result.items) {
        collection.utils.writeUpsert(item);
      }
    });

    return { items: [...result.items], totalCount: result.totalCount, page };
  };

  return { collection, fetchPage, queryScope: scope };
}

export type SupportTicketListCollectionWithLoader = ReturnType<
  typeof createSupportTicketListCollection
>;
